<script setup lang="ts">
import { ref, computed } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useComponentComposable } from '@/components/useComponentComposable';
import Checkbox from 'primevue/checkbox';
import Select from 'primevue/select';

interface Props {
  isDefaultSeason: boolean;
  startDay: string;
  startMonth: string;
  endDay: string;
  endMonth: string;
  seasonalDefault: any;
  isSeasonalException?: boolean;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  'update:startDay': [value: any];
  'update:startMonth': [value: any];
  'update:endDay': [value: any];
  'update:endMonth': [value: any];
  'update:isDefaultSeason': [value: any];
}>();

const currentLanguage = getCurrentLocale();
const { renderListMonth, checkValidity, displayDate } = useComponentComposable();

const startDayData = computed({
  get() {
    return props.startDay;
  },
  set(value) {
    emits('update:startDay', value);
  }
});

const startMonthData = computed({
  get() {
    return props.startMonth;
  },
  set(value) {
    emits('update:startMonth', value);
  }
});

const endDayData = computed({
  get() {
    return props.endDay;
  },
  set(value) {
    emits('update:endDay', value);
  }
});

const endMonthData = computed({
  get() {
    return props.endMonth;
  },
  set(value) {
    emits('update:endMonth', value);
  }
});

const isDefaultSeasonData = computed({
  get() {
    return props.isDefaultSeason;
  },
  set(value) {
    emits('update:isDefaultSeason', value);
  }
});

const defaultDate = ref<boolean>(isDefaultSeasonData.value);
const specificDate = ref<boolean>(!isDefaultSeasonData.value);

const filterOption = (data: number) => {
  return Array.from({ length: data }, (_, i) => ({ label: data === 12 ? renderListMonth(currentLanguage)[i] : i + 1, value: (i + 1).toString() }));
};

const updateSeason = (isDefault: boolean | null) => {
  emits('update:isDefaultSeason', isDefault);
  emits('update:startDay', '');
  emits('update:startMonth', '');
  emits('update:endDay', '');
  emits('update:endMonth', '');
};

const validateCheckBox = (_event: any, type: string) => {
  if (type === 'DEFAULT') {
    defaultDate.value = true;
    specificDate.value = false;
    updateSeason(true);
  } else if (type === 'SPECIFIC' && !props.isSeasonalException) {
    defaultDate.value = false;
    specificDate.value = true;
    updateSeason(false);
  } else {
    specificDate.value = !specificDate.value;
    updateSeason(!specificDate.value);
  }
};

const getDaysDefault = (month = 1) => {
  const year = new Date().getFullYear();
  const lastDayOfMonth = new Date(year, month, 0).getDate();
  const daysArray = [];

  for (let i = 1; i <= lastDayOfMonth; i++) {
    daysArray.push({
      label: i.toString(),
      value: i.toString()
    });
  }
  return daysArray;
};
</script>
<template>
  <div class="seasonal-selected">
    <div>
      <div class="default_selected">
        <div class="flex" v-if="!isSeasonalException">
          <Checkbox id="default_date" :modelValue="defaultDate" :binary="true" @update:modelValue="validateCheckBox($value, 'DEFAULT')" />
          <div>
            <p class="ml-3 mb-0">{{ `${$t('common.labels.use_default_date')}` }}</p>
            <p class="ml-3 mb-0">
              ({{
                checkValidity(seasonalDefault)
                  ? `${displayDate(seasonalDefault.startDay, seasonalDefault.startMonth, currentLanguage, '')} - ${displayDate(seasonalDefault.endDay, seasonalDefault.endMonth, currentLanguage, '')}`
                  : $t('common.labels.format_selected')
              }})
            </p>
          </div>
        </div>
      </div>
      <div>
        <div class="flex mt-2">
          <Checkbox id="specific_date" :binary="true" :modelValue="specificDate" @update:modelValue="validateCheckBox($value, 'SPECIFIC')" />
          <p class="ml-3">{{ $t('common.labels.create_specific_date') }}</p>
        </div>
        <div class="flex gap-2 mt-3" v-if="specificDate">
          <div>
            <p class="mb-0">{{ $t('common.labels.start_month') }}</p>
            <Select id="start_month" scrollHeight="300px" v-model="startMonthData" :options="filterOption(12)" optionLabel="label" optionValue="value" class="w-7rem">
              <template #option="slotProps">
                <p>
                  {{ slotProps.option.label }}
                </p>
              </template>
            </Select>
          </div>
          <div>
            <p class="mb-0">{{ $t('common.labels.start_day') }}</p>
            <Select id="start_day" scrollHeight="300px" :disabled="!startMonthData" v-model="startDayData" :options="getDaysDefault(startMonthData)" optionLabel="label" optionValue="value" class="w-7rem">
              <template #option="slotProps">
                <p>
                  {{ slotProps.option.label }}
                </p>
              </template>
            </Select>
          </div>
        </div>
        <div class="flex gap-2 mt-3" v-if="specificDate">
          <div>
            <p class="mb-0">{{ $t('common.labels.end_month') }}</p>
            <Select id="end_month" scrollHeight="300px" v-model="endMonthData" :options="filterOption(12)" optionLabel="label" optionValue="value" class="w-7rem">
              <template #option="slotProps">
                <p>
                  {{ slotProps.option.label }}
                </p>
              </template>
            </Select>
          </div>
          <div>
            <p class="mb-0">{{ $t('common.labels.end_day') }}</p>
            <Select id="end_day" scrollHeight="300px" :disabled="!endMonthData" v-model="endDayData" :options="getDaysDefault(endMonthData)" optionLabel="label" optionValue="value" class="w-7rem">
              <template #option="slotProps">
                <p>
                  {{ slotProps.option.label }}
                </p>
              </template>
            </Select>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
